import { useState, useEffect } from 'react'

import { Menu } from '@mantine/core'

import IconChevronDown from 'src/icons/IconChevronDown'
import {
  CountryConfig,
  CountryConfigItem,
  findIssuingCountry,
} from 'src/lib/country.utils'
import { buildFullName } from 'src/lib/formatters'

import ApplicationNumberInput from '../components/ApplicationNumberInput'
import ApplicationPasswordInput from '../components/ApplicationPasswordInput'
import ApplicationScreenWrapper from '../components/ApplicationScreenWrapper'
import ApplicationTextInput from '../components/ApplicationTextInput'
import { useFormLeaveGuard } from '../hooks/useLeaveGuard'
import { formatDate, formatSsn, formatDateString } from '../utils/formatters'
import { useFormContext } from '../utils/FormContext'
import {
  buildNamePayload,
  buildPersonalDetailsPayload,
} from '../utils/stepPayloads'

const isMobile = window.innerWidth < 640

export const NameInformationScreen = ({ updateLoading }) => {
  const { form, handleNext, application } = useFormContext()
  const { goBack, leaveGuardModal } = useFormLeaveGuard(buildNamePayload)

  return (
    <ApplicationScreenWrapper
      title={'What is your legal name?'}
      description={'Please confirm the following information.'}
      goBack={goBack}
      application={application}
      hideBackButton={true}
      updateLoading={updateLoading}
      saveButtontext={'Save and continue'}
      handleSubmit={() => {
        handleNext(buildNamePayload(form.values))
      }}
    >
      {leaveGuardModal}
      <ApplicationTextInput
        placeholder="First Name"
        required
        label="First Name"
        {...form.getInputProps('firstName')}
      />
      <ApplicationTextInput
        label="Middle Name"
        placeholder="Middle Name"
        {...form.getInputProps('middleName')}
      />
      <ApplicationTextInput
        label="Last Name"
        required
        placeholder="Last Name"
        {...form.getInputProps('lastName')}
      />
    </ApplicationScreenWrapper>
  )
}

export const PersonalInformationScreen = () => {
  const { form, handleNext, updateLoading, application, backText, fromReview } =
    useFormContext()
  const { goBack, leaveGuardModal } = useFormLeaveGuard(
    buildPersonalDetailsPayload
  )

  const existingIssuingCountry = application?.issuingCountry
    ? findIssuingCountry(application.issuingCountry)
    : undefined
  const [issuingCountryConfig, setIssuingCountry] = useState<CountryConfigItem>(
    existingIssuingCountry || CountryConfig.US
  )

  const leftSectionWidth = '80px'
  const leftSectionStyles = `flex flex-row items-center gap-1 text-nowrap px-2 font-medium text-doubleNickel-gray-800 hover:cursor-pointer ${
    isMobile ? 'text-lg' : 'text-sm'
  }`

  useEffect(() => {
    if (!form.values.ssn) return
    formatSsn(issuingCountryConfig.code, form.values.ssn, form)
  }, [issuingCountryConfig.code])

  return (
    <ApplicationScreenWrapper
      title={'Hello ' + buildFullName(application) + '!'}
      description={'Please complete the following information'}
      goBack={goBack}
      hideBackButton={!fromReview}
      application={application}
      updateLoading={updateLoading}
      saveButtontext={'Save and continue'}
      backText={backText}
      handleCancel={goBack}
      handleSubmit={() => {
        handleNext(buildPersonalDetailsPayload(form.values))
      }}
    >
      {leaveGuardModal}
      <ApplicationTextInput
        label="Email Address"
        required
        placeholder="Email"
        {...form.getInputProps('email')}
      />
      <ApplicationTextInput
        label="Date of Birth"
        required
        key="dob"
        pattern="\d*"
        placeholder={'MM / DD / YYYY'}
        {...form.getInputProps('dob')}
        value={formatDateString(form.values.dob)}
        onChange={(event) => {
          formatDate('dob', event, form)
        }}
        error={form.errors['dob']}
      />
      <ApplicationPasswordInput
        label={issuingCountryConfig.ssnTitle}
        pattern="\d*"
        inputMode="numeric"
        required
        placeholder={issuingCountryConfig.ssnFormatPlaceholder}
        leftSectionWidth={leftSectionWidth}
        leftSectionPointerEvents={'all'}
        leftSection={
          <Menu shadow="md">
            <Menu.Target>
              <div className={leftSectionStyles}>
                {issuingCountryConfig.label}
                <IconChevronDown className="h-4 fill-none stroke-doubleNickel-gray-400" />
              </div>
            </Menu.Target>
            <Menu.Dropdown>
              {Object.values(CountryConfig).map((config) => (
                <Menu.Item
                  key={config.code}
                  onClick={() => setIssuingCountry(config)}
                >
                  <div>{config.label}</div>
                </Menu.Item>
              ))}
            </Menu.Dropdown>
          </Menu>
        }
        {...form.getInputProps('ssn')}
        onChange={(event) => {
          formatSsn(issuingCountryConfig.code, event.target.value, form)
        }}
      />
      <ApplicationTextInput
        label="Available Start Date"
        pattern="\d*"
        placeholder={'MM / DD / YYYY'}
        value={formatDateString(form.values.availableForWork)}
        onChange={(event) => {
          formatDate('availableForWork', event, form)
        }}
        error={form.errors.availableForWork}
      />
      <ApplicationNumberInput
        label="Years of Experience"
        decimalScale={1}
        decimalSeparator="."
        min={0}
        placeholder="Example: 2.5"
        inputMode="decimal"
        {...form.getInputProps('yearsOfExperience')}
      />
    </ApplicationScreenWrapper>
  )
}
